/**
 * BaseModel class serves as an abstract base class for models. It provides
 * functionality for handling form data generation, setting data, marking
 * initialization states, and managing mutable states.
 *
 * Example Usage:
 * ```
 * import BaseModel from "./Abstracts/BaseModel";
 *
 * export default class MyModel extends BaseModel{
 *     constructor(data) {
 *         super()
 *         this.name = ''
 *         this.description = ''
 *         this.doNotPostMe = 'internal data'
 *
 *         this._skipKeys.push('doNotPostMe')
 *         this.setData(data)
 *         this.setInitialized()
 *     }
 *
 *     _getAdditionalFormData() {
 *         return {
 *             'moreData': 'something to send back to the server'
 *         }
 *     }
 * }
 *
 * const model = new MyModel({name: 'Name', description: 'description', doNotPostMe: 'the server does not need to know about this data'})
 *
 * const data = model.getFormData()
 * axios.post(updateURL, data)
 * ```
 */
export default class BaseModel {
    constructor() {
        // any keys in _skipKeys will not be included when calling getFormData
        this._skipKeys = ['$dirty', 'id', '_skipKeys', '$initialized', '_getAdditionalFormData']

        // we do not want to be able to directly instantiate a BaseModel instance as it would serve
        if (new.target === BaseModel) {
            throw new TypeError("Cannot construct BaseModel instances directly")
        }

        this.$dirty = false
        this.$initialized = false
        this._getAdditionalFormData = null
    }

    _setRandomID() {
        this.id = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        )
    }

    getFormData() {
        const formData = new FormData()

        for (const key in this) {
            if (this._skipKeys.indexOf(key) !== -1) {
                continue
            }
            if (this.hasOwnProperty(key)) {
                formData.append(key, this[key])
            }
        }
        
        // If the _getAdditionalFormData function exists, call it and append its data
        if (typeof this._getAdditionalFormData === 'function') {
            const additionalData = this._getAdditionalFormData();
            for (const key in additionalData) {
                if (additionalData.hasOwnProperty(key)) {
                    formData.append(key, additionalData[key]);
                }
            }
        }

        return formData
    }

    setData(data) {
        for (let key in data) {
            if (data.hasOwnProperty(key)) {
                if (this.$initialized && this[key] !== data[key]) {
                    this.$dirty = true
                }
                this[key] = data[key]
            }
        }
    }

    // this should be called after the initial setData
    setInitialized() {
        this.$dirty = false
        this.$initialized = true
    }
}